/**
 * LA VILLE À PARTIR DU CODE POSTAL — ce qu'on remplit, et ce qu'on ne touche pas.
 *
 * Signalé par Amine le 19 septembre 2026 : « je mets des codes postaux, ça ne
 * me met pas les bonnes villes ». Deux causes, en une seule ligne de code :
 * l'ancien remplissage prenait la PREMIÈRE commune rendue par l'API (un code
 * postal en couvre souvent plusieurs — 97231 : Le Robert, mais aussi ses
 * hameaux ; 76190 : dix communes), et il se déclenchait aussi à l'OUVERTURE
 * de la fiche, écrasant la ville enregistrée par cette première commune.
 *
 * La règle, testable sans réseau :
 *  - la ville déjà saisie fait foi si elle fait partie des communes du code ;
 *  - une seule commune : on la remplit ;
 *  - plusieurs, et la ville actuelle n'en est pas : on remplit la première
 *    SEULEMENT si le champ est vide, et on propose la liste entière.
 */

export interface DecisionVille {
    /** La ville à écrire dans le champ — `null` : ne pas y toucher. */
    ville: string | null;
    /** Les communes à proposer (liste déroulante). */
    propositions: string[];
}

function normaliser(v: string | null | undefined): string {
    return String(v ?? '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[-']/g, ' ').replace(/\s+/g, ' ')
        .trim().toLowerCase();
}

export function decideLaVille(villeActuelle: string | null | undefined, communes: string[]): DecisionVille {
    const propositions = [...new Set(communes.map((c) => String(c ?? '').trim()).filter(Boolean))];
    if (propositions.length === 0) return { ville: null, propositions: [] };

    const actuelle = normaliser(villeActuelle);
    if (actuelle) {
        const dejaBonne = propositions.find((c) => normaliser(c) === actuelle);
        if (dejaBonne) return { ville: null, propositions };
    }
    if (propositions.length === 1) return { ville: propositions[0], propositions };
    return { ville: actuelle ? null : propositions[0], propositions };
}

/** Un code postal français : cinq chiffres, rien d'autre. */
export function estUnCodePostalFrancais(valeur: string | null | undefined): boolean {
    return /^\d{5}$/.test(String(valeur ?? '').trim());
}
